import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Trophy, TrendingUp, DollarSign, Route as RouteIcon, ChevronDown, ChevronUp,
  ChevronLeft, ChevronRight, Truck as TruckIcon, ArrowUp, ArrowDown, Info, HandCoins,
} from "lucide-react";
import { apiBaseUrl, authHeaders } from "./shared";
import LoadingState from "./LoadingState";

const RANK_STYLE = [
  { bg: "linear-gradient(135deg, #f4c94a, #d99c1f)", ring: "rgba(244,201,74,0.35)", label: "1st" },
  { bg: "linear-gradient(135deg, #c9d2dc, #9aa7b4)", ring: "rgba(201,210,220,0.35)", label: "2nd" },
  { bg: "linear-gradient(135deg, #d79a63, #a8663a)", ring: "rgba(215,154,99,0.35)", label: "3rd" },
];

function fmtMiles(n) {
  return n == null ? "—" : `${Math.round(n).toLocaleString()} mi`;
}
function fmtMoney(n) {
  return n == null ? "—" : `${n < 0 ? "-" : ""}$${Math.round(Math.abs(n)).toLocaleString()}`;
}
function fmtRate(n) {
  return n == null ? "—" : `$${n.toFixed(2)}/mi`;
}

// The three ranked boards this page can show. Each pulls from a different
// server-computed dataset -- "booked" (dispatcher_revenue_booked_this_week,
// attributed by loads.created_at -- when the load was actually negotiated)
// vs "worked" (dispatcher_performance_this_week, attributed by
// pickup_datetime -- when the miles/cost were actually real). Deliberately
// NOT one blended score: a composite would need an arbitrary weighting
// between "negotiated well" and "ran efficiently" that's easy to argue with
// and hard to defend, so each stays its own honest number.
const BOARDS = [
  {
    key: "revenue_booked",
    label: "Revenue Booked",
    icon: HandCoins,
    source: "booked",
    metricKey: "revenue_booked",
    fmt: fmtMoney,
    metricLabel: "booked",
    description:
      "Straight sum of the negotiated rate on every load this dispatcher booked this week -- counted the week the load was BOOKED (created with a truck on it), not the week it actually ran. This is the pure sales/negotiating number, with no cost or mileage netted out.",
  },
  {
    key: "profit_per_truck",
    label: "Net Profit per Truck",
    icon: TrendingUp,
    source: "worked",
    metricKey: "net_profit_per_truck",
    fmt: fmtMoney,
    metricLabel: "net profit/truck",
    description:
      "Real profit (revenue minus fuel, driver pay, tolls, insurance, maintenance), plus this week's real fixed equipment cost ($1,200/truck/week) of EVERY truck actually assigned to this dispatcher -- not just the ones that happened to run a load. Divided by that same full owned-truck count. A truck sitting idle all week, or one that ran but didn't cover its own $1,200, now actually drags this number down -- it can no longer hide behind a dispatcher's busier trucks.",
  },
  {
    key: "price_per_mile",
    label: "$/Mile (all miles)",
    icon: RouteIcon,
    source: "worked",
    metricKey: "price_per_mile",
    fmt: fmtRate,
    metricLabel: "avg rate/mi",
    description:
      "Revenue divided by ALL miles driven this week -- including deadhead/empty miles to reach the pickup, not just the loaded leg. A great negotiated rate reached by driving 300 empty miles to get there scores WORSE here than a modest rate with a tight backhaul. This is the efficiency number: it rewards good lane planning, not just good negotiating.",
  },
];

function RankBadge({ index }) {
  const s = RANK_STYLE[index] || { bg: "var(--bg2, #eef1f4)", ring: "transparent", label: `${index + 1}th` };
  return (
    <div
      className={index === 0 ? "lb-leader-pulse" : undefined}
      style={{
        width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
        background: s.bg, boxShadow: index < 3 ? `0 0 0 5px ${s.ring}` : "none",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontWeight: 800, fontSize: 14, color: index < 3 ? "#3a2a08" : "var(--muted)",
        border: index < 3 ? "none" : "1.5px solid var(--line)",
      }}
    >
      {index < 3 ? <Trophy size={16} strokeWidth={2.5} /> : `#${index + 1}`}
    </div>
  );
}

// How many spots this dispatcher moved vs last week's SAME board ranking --
// null means "wasn't ranked at all last week" (shown as NEW), 0 is a real,
// legitimate "held their spot" and intentionally renders nothing rather than
// a static "0" badge, since a badge that never changes isn't worth showing.
function RankChangeBadge({ change }) {
  // undefined -- previous week's data hasn't loaded yet, show nothing
  // rather than a premature/wrong badge. null -- confirmed not ranked at
  // all last week, a real "NEW" (not the same as "held steady").
  if (change === undefined) return null;
  if (change === null) {
    return <span style={{ ...styles.rankChangeBadge, color: "var(--brand)", background: "rgba(29, 111, 216,0.1)" }}>NEW</span>;
  }
  if (change === 0) return null;
  const up = change > 0;
  return (
    <span style={{ ...styles.rankChangeBadge, color: up ? "#4cae6d" : "#e0745d", background: up ? "rgba(76,174,109,0.1)" : "rgba(224,116,93,0.1)" }}>
      {up ? <ArrowUp size={9} /> : <ArrowDown size={9} />}
      {Math.abs(change)}
    </span>
  );
}

const DAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"];

function trendPct(curr, prev) {
  if (!prev || curr == null) return null;
  return Math.round(((curr - prev) / prev) * 100);
}

function TrendBadge({ pct }) {
  if (pct == null) return null;
  const up = pct >= 0;
  return (
    <span style={{ ...styles.trendBadge, color: up ? "#4cae6d" : "#e0745d" }}>
      {up ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
      {Math.abs(pct)}%
    </span>
  );
}

function hexToRgba(hex, alpha) {
  const h = hex.replace("#", "");
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

// Small click-to-toggle explanation popover -- one open at a time, closed by
// clicking anywhere else. Used both on each board tab (explain the ranking
// metric before switching to it) and isn't tied to any one board's markup,
// so the same component covers both spots the ask called for.
function InfoTooltip({ id, openId, setOpenId, text, align = "left" }) {
  const wrapRef = useRef(null);
  const open = openId === id;

  useEffect(() => {
    if (!open) return;
    function onDocClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpenId(null);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open, setOpenId]);

  return (
    <span ref={wrapRef} style={styles.infoWrap}>
      <button
        type="button"
        aria-label="What does this mean?"
        style={{ ...styles.infoBtn, ...(open ? styles.infoBtnActive : {}) }}
        onClick={(e) => {
          e.stopPropagation();
          setOpenId(open ? null : id);
        }}
      >
        <Info size={12} strokeWidth={2.5} />
      </button>
      {open && (
        <div style={{ ...styles.infoPopover, ...(align === "right" ? { right: 0 } : { left: 0 }) }}>
          {text}
        </div>
      )}
    </span>
  );
}

function DayBar({ dailyMiles, color }) {
  const max = Math.max(0, ...dailyMiles);
  const peakIdx = max > 0 ? dailyMiles.indexOf(max) : -1;
  return (
    <div style={styles.dayBarRow}>
      {dailyMiles.map((m, i) => {
        const isPeak = i === peakIdx;
        return (
          <div key={i} style={styles.dayBarCol} title={`${DAY_LABELS[i]}: ${Math.round(m)} mi`}>
            <div style={styles.dayBarTrack}>
              <div
                style={{
                  ...styles.dayBarFill,
                  height: m ? `${Math.max(12, (m / max) * 100)}%` : 3,
                  background: m ? (isPeak ? color : hexToRgba(color, 0.32)) : "var(--line)",
                }}
              />
            </div>
            <span style={{ ...styles.dayBarLabel, ...(isPeak ? styles.dayBarLabelPeak : {}) }}>
              {DAY_LABELS[i]}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function RaceBar({ pct, color }) {
  return (
    <div style={styles.raceTrack}>
      <div
        style={{
          ...styles.raceFill,
          width: `${Math.max(2, Math.min(100, pct))}%`,
          background: color,
        }}
      />
    </div>
  );
}

function TruckRow({ truck, maxMiles, color }) {
  const pct = maxMiles ? (truck.miles / maxMiles) * 100 : 0;
  return (
    <div style={styles.truckRow}>
      <div style={styles.truckRowHead}>
        <span style={styles.truckIconWrap}><TruckIcon size={12} /></span>
        <span style={styles.truckUnit}>#{truck.unit_number}</span>
        <span style={styles.truckMiles}>{fmtMiles(truck.miles)}</span>
        <span style={styles.truckRate}>{fmtRate(truck.price_per_mile)}</span>
        <span style={styles.truckLoads}>{truck.loads_count} load{truck.loads_count === 1 ? "" : "s"}</span>
      </div>
      <RaceBar pct={pct} color={color} />
    </div>
  );
}

function formatShortDate(isoLike) {
  if (!isoLike) return "—";
  const d = new Date(isoLike);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
}

// Per-load detail row on the Revenue Booked board -- the point is letting
// any dispatcher see exactly what lane/rate a top-ranked colleague booked
// this week, not just the aggregate number, so it's actually learnable
// from ("copy him next week") rather than just a scoreboard.
function BookedLoadRow({ load }) {
  return (
    <div style={styles.loadRow}>
      <div style={styles.loadRowLane}>
        {load.unit_number && <span style={styles.loadRowUnit}>#{load.unit_number}</span>}
        <span style={styles.loadRowRoute}>{load.origin || "?"} <ArrowUp size={9} style={styles.loadRowArrow} /> {load.destination || "?"}</span>
      </div>
      <span style={styles.loadRowDate}>{formatShortDate(load.pickup_datetime)}</span>
      <span style={styles.loadRowMiles}>{load.miles != null ? fmtMiles(load.miles) : "—"}</span>
      <span style={styles.loadRowRate}>{load.price_per_mile != null ? fmtRate(load.price_per_mile) : "—"}</span>
      <span style={styles.loadRowRevenue}>{fmtMoney(load.rate_offered)}</span>
    </div>
  );
}

function DispatcherCard({ board, d, prev, rankChange, index, maxValue, color }) {
  const isWorked = board.source === "worked";
  const [open, setOpen] = useState(index === 0);
  const value = d[board.metricKey];
  const pct = maxValue ? ((value ?? 0) / maxValue) * 100 : 0;
  const trend = trendPct(value, prev?.[board.metricKey]);
  const negative = value != null && value < 0;

  return (
    <div style={{ ...styles.card, ...(index === 0 ? styles.cardTop : {}) }}>
      <div style={styles.cardHead} onClick={() => setOpen((v) => !v)}>
        <div style={styles.rankStack}>
          <RankBadge index={index} />
          <RankChangeBadge change={rankChange} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={styles.dispatcherName}>{d.dispatcher_name}</div>
          <RaceBar pct={pct} color={color} />
          {isWorked && d.daily_miles && <DayBar dailyMiles={d.daily_miles} color={color} />}
          {!isWorked && (
            <div style={styles.bookedSubline}>
              {d.loads_count} load{d.loads_count === 1 ? "" : "s"} booked this week -- click to see the lanes
            </div>
          )}
        </div>
        <div style={styles.statBlock}>
          <div style={{ ...styles.statBig, ...(negative ? { color: "#e0745d" } : {}) }}>
            {board.fmt(value)}
          </div>
          <div style={styles.statSmallLabel}>{board.metricLabel}</div>
          <TrendBadge pct={trend} />
        </div>
        {isWorked && (
          <>
            <div style={styles.statBlock}>
              <div style={styles.statBig}>{fmtMiles(d.total_miles)}</div>
              <div style={styles.statSmallLabel}>miles</div>
            </div>
            <div style={styles.statBlock}>
              <div style={{ ...styles.statBig, color: "var(--brand)" }}>{fmtMoney(d.net_profit)}</div>
              <div style={styles.statSmallLabel}>net profit</div>
            </div>
          </>
        )}
        <div style={styles.chevronWrap}>{open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</div>
      </div>
      {isWorked && open && (
        <div style={styles.truckList}>
          {d.trucks.map((t) => (
            <TruckRow key={t.truck_id} truck={t} maxMiles={d.trucks[0]?.miles || 1} color={color} />
          ))}
        </div>
      )}
      {!isWorked && open && (
        <div style={styles.loadList}>
          {(!d.loads || d.loads.length === 0) && <div style={styles.loadListEmpty}>No load detail available.</div>}
          {d.loads?.length > 0 && (
            <div style={styles.loadRowHead}>
              <span style={{ flex: 1 }}>Lane</span>
              <span style={styles.loadRowDate}>Pickup</span>
              <span style={styles.loadRowMiles}>Miles</span>
              <span style={styles.loadRowRate}>$/mi</span>
              <span style={styles.loadRowRevenue}>Rate</span>
            </div>
          )}
          {d.loads?.map((l) => <BookedLoadRow key={l.load_id} load={l} />)}
        </div>
      )}
    </div>
  );
}

const PALETTE = ["#1d6fd8", "#5b9bd5", "#a78bfa", "#f2a33d", "#4cae6d", "#e0745d"];

// Adds `days` to a YYYY-MM-DD date string, staying in plain calendar-date
// arithmetic (no timezone/UTC conversion involved, unlike Date.toISOString)
// -- safe to use once we have a real anchor date, but never used to guess
// what "today" is in the first place (see anchorMonday below).
function addDaysIso(iso, days) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d + days);
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${dt.getFullYear()}-${mm}-${dd}`;
}

export default function DispatcherPerformance() {
  const [boardKey, setBoardKey] = useState(BOARDS[0].key);
  const [infoOpenId, setInfoOpenId] = useState(null);
  const board = BOARDS.find((b) => b.key === boardKey) || BOARDS[0];

  const [weekOffset, setWeekOffset] = useState(0);
  // The real Monday of "this week," per the SERVER's own clock -- learned
  // from the first response's period_start rather than computed from the
  // browser's Date(), which can't be trusted to agree with the server on
  // what day it is (confirmed real: a sandboxed browser tab's clock landed
  // a full week off from the backend's, silently showing last week's
  // numbers as "this week"). Every later prev/next click offsets from
  // this server-confirmed anchor, not a fresh guess.
  const [anchorMonday, setAnchorMonday] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setData(null);
    setError(null);
    const weekStart = weekOffset === 0 ? null : addDaysIso(anchorMonday, weekOffset * 7);
    const url = weekStart
      ? `${apiBaseUrl}/scripted/fleet/dispatcher-performance?week_start=${weekStart}`
      : `${apiBaseUrl}/scripted/fleet/dispatcher-performance`;
    axios
      .get(url, authHeaders())
      .then((res) => {
        setData(res.data);
        if (weekOffset === 0) setAnchorMonday(res.data.period_start.slice(0, 10));
      })
      .catch(() => setError("Couldn't load that week's dispatcher performance."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekOffset]);

  // The week immediately before whichever one is showing, keyed by
  // dispatcher_id -- purely for the trend arrows, fetched off the
  // currently-loaded week's own period_start (not weekOffset/anchorMonday
  // directly) so this works correctly no matter how the current week was
  // reached. Holds BOTH datasets (worked + booked) since either board's
  // trend badge might need it.
  const [prevWorkedById, setPrevWorkedById] = useState(null);
  const [prevBookedById, setPrevBookedById] = useState(null);
  useEffect(() => {
    if (!data) {
      setPrevWorkedById(null);
      setPrevBookedById(null);
      return;
    }
    const prevStart = addDaysIso(data.period_start.slice(0, 10), -7);
    axios
      .get(`${apiBaseUrl}/scripted/fleet/dispatcher-performance?week_start=${prevStart}`, authHeaders())
      .then((res) => {
        const workedMap = {};
        (res.data.dispatchers || []).forEach((d) => {
          workedMap[d.dispatcher_id || "unassigned"] = d;
        });
        const bookedMap = {};
        (res.data.booked_dispatchers || []).forEach((d) => {
          bookedMap[d.dispatcher_id || "unassigned"] = d;
        });
        setPrevWorkedById(workedMap);
        setPrevBookedById(bookedMap);
      })
      .catch(() => {
        setPrevWorkedById(null);
        setPrevBookedById(null);
      });
  }, [data]);

  const worked = data?.dispatchers || [];
  const booked = data?.booked_dispatchers || [];
  const activeList = board.source === "booked" ? booked : worked;
  const prevById = board.source === "booked" ? prevBookedById : prevWorkedById;
  const ranked = [...activeList].sort((a, b) => (b[board.metricKey] ?? -Infinity) - (a[board.metricKey] ?? -Infinity));
  const maxValue = Math.max(1, ...ranked.map((d) => d[board.metricKey] ?? 0));

  // Real rank-change vs last week, same board/metric -- re-derives last
  // week's own ranking from the already-fetched prev data rather than
  // trusting any order the server happened to return it in.
  const prevRankByKey = prevById
    ? Object.fromEntries(
        Object.values(prevById)
          .sort((a, b) => (b[board.metricKey] ?? -Infinity) - (a[board.metricKey] ?? -Infinity))
          .map((d, i) => [d.dispatcher_id || "unassigned", i])
      )
    : null;
  function rankChangeFor(dispatcherKey, currentIndex) {
    if (!prevRankByKey) return undefined;
    const prevIndex = prevRankByKey[dispatcherKey];
    return prevIndex == null ? null : prevIndex - currentIndex;
  }

  // miles/revenue/loads are safe to sum client-side -- each load belongs to
  // exactly one dispatcher bucket, so these are just real additive totals.
  // profit is NOT summed here -- data.company_money.profit (below) is the
  // canonical fleet-wide number instead, straight from fleet_stats_summary
  // (same source the Reports page uses). Summing each dispatcher's own
  // `profit` would overstate the fleet total: it's blind to idle/
  // underutilized assigned-truck equipment cost (see net_profit), and
  // summing net_profit instead would silently miss any truck with no
  // dispatcher at all (real cost still hits the company, nobody
  // individually) -- see dispatcher_performance_this_week's own docstring.
  const fleetTotals = worked.reduce(
    (acc, d) => ({
      miles: acc.miles + d.total_miles,
      revenue: acc.revenue + d.revenue,
      loads: acc.loads + d.loads_count,
    }),
    { miles: 0, revenue: 0, loads: 0 }
  );
  const fleetProfit = data?.company_money?.profit ?? null;
  const periodLabel = data
    ? `${new Date(data.period_start).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })} – ${new Date(
        new Date(data.period_end).getTime() - 86400000
      ).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })}`
    : "";

  return (
    <div style={styles.page}>
      {PULSE_STYLE_TAG}
      <div style={styles.header}>
        <div>
          <div style={styles.eyebrow}>WEEKLY LEADERBOARD</div>
          <h1 style={styles.title}>Dispatcher Performance</h1>
          <div style={styles.weekNav}>
            <button
              style={{ ...styles.weekNavBtn, opacity: anchorMonday ? 1 : 0.35 }}
              onClick={() => anchorMonday && setWeekOffset((v) => v - 1)}
              disabled={!anchorMonday}
              title="Previous week"
            >
              <ChevronLeft size={16} color="var(--ink, #1a1a1a)" strokeWidth={2.5} />
            </button>
            <span style={styles.subtitle}>{periodLabel ? `Week of ${periodLabel}` : "Loading…"}</span>
            <button
              style={{ ...styles.weekNavBtn, opacity: weekOffset >= 0 ? 0.35 : 1, cursor: weekOffset >= 0 ? "default" : "pointer" }}
              onClick={() => weekOffset < 0 && setWeekOffset((v) => v + 1)}
              disabled={weekOffset >= 0}
              title="Next week"
            >
              <ChevronRight size={16} color="var(--ink, #1a1a1a)" strokeWidth={2.5} />
            </button>
            {weekOffset !== 0 && (
              <button style={styles.weekNavThisWeek} onClick={() => setWeekOffset(0)}>
                This week
              </button>
            )}
          </div>
        </div>
        {data && (
        <div style={styles.fleetStrip}>
          <div style={styles.fleetStat}>
            <RouteIcon size={14} />
            <div>
              <div style={styles.fleetStatValue}>{fmtMiles(fleetTotals.miles)}</div>
              <div style={styles.fleetStatLabel}>fleet miles</div>
            </div>
          </div>
          <div style={styles.fleetStat}>
            <TrendingUp size={14} />
            <div>
              <div style={styles.fleetStatValue}>{fmtMoney(fleetProfit)}</div>
              <div style={styles.fleetStatLabel}>fleet profit</div>
            </div>
          </div>
          <div style={styles.fleetStat}>
            <DollarSign size={14} />
            <div>
              <div style={styles.fleetStatValue}>{fleetTotals.miles ? fmtRate(fleetTotals.revenue / fleetTotals.miles) : "—"}</div>
              <div style={styles.fleetStatLabel}>fleet avg rate/mi</div>
            </div>
          </div>
        </div>
        )}
      </div>

      {!data && !error && <LoadingState label="Loading dispatcher performance…" />}

      <div style={styles.boardTabs}>
        {BOARDS.map((b) => {
          const Icon = b.icon;
          const activeTab = b.key === boardKey;
          return (
            <button
              key={b.key}
              type="button"
              style={{ ...styles.boardTab, ...(activeTab ? styles.boardTabActive : {}) }}
              onClick={() => setBoardKey(b.key)}
            >
              <Icon size={14} />
              {b.label}
              <InfoTooltip id={b.key} openId={infoOpenId} setOpenId={setInfoOpenId} text={b.description} />
            </button>
          );
        })}
      </div>

      {!error && ranked.length > 0 && (
        <div style={styles.leaderStrip}>
          <div className="lb-leader-pulse" style={styles.leaderIconWrap}>
            <Trophy size={16} strokeWidth={2.5} />
          </div>
          <div style={styles.leaderText}>
            <span style={styles.leaderName}>{ranked[0].dispatcher_name}</span> leads {board.label.toLowerCase()} this week with{" "}
            <span style={styles.leaderValue}>{board.fmt(ranked[0][board.metricKey])}</span>
            {ranked.length > 1 && (
              <span style={styles.leaderGap}>
                {" "}-- {board.fmt(Math.abs(ranked[0][board.metricKey] - (ranked[1][board.metricKey] ?? 0)))} ahead of 2nd place
              </span>
            )}
          </div>
        </div>
      )}

      {error && <div style={styles.emptyState}>{error}</div>}
      {!error && data && ranked.length === 0 && (
        <div style={styles.emptyState}>
          {board.source === "booked"
            ? "No loads booked yet this week -- the board fills in as loads get created."
            : "No completed or in-progress loads yet this week -- the board fills in as real trips run."}
        </div>
      )}

      <div style={styles.board}>
        {ranked.map((d, i) => (
          <DispatcherCard
            key={d.dispatcher_id || "unassigned"}
            board={board}
            d={d}
            prev={prevById?.[d.dispatcher_id || "unassigned"]}
            rankChange={rankChangeFor(d.dispatcher_id || "unassigned", i)}
            index={i}
            maxValue={maxValue}
            color={PALETTE[i % PALETTE.length]}
          />
        ))}
      </div>
    </div>
  );
}

// Injected once -- the #1 spot gets a slow, subtle glow pulse (never a
// jarring animation), off entirely for anyone with reduced-motion set.
const PULSE_STYLE_TAG = (
  <style>{`
    @keyframes lb-leader-pulse {
      0%, 100% { box-shadow: 0 0 0 5px rgba(244,201,74,0.35); }
      50% { box-shadow: 0 0 0 8px rgba(244,201,74,0.55); }
    }
    .lb-leader-pulse { animation: lb-leader-pulse 2.6s ease-in-out infinite; }
    @media (prefers-reduced-motion: reduce) {
      .lb-leader-pulse { animation: none; }
    }
  `}</style>
);

const styles = {
  page: { padding: "24px 28px 40px", maxWidth: 1000, margin: "0 auto" },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-end",
    flexWrap: "wrap", gap: 20, marginBottom: 20,
  },
  eyebrow: { fontSize: 10.5, fontWeight: 700, letterSpacing: 1.2, color: "var(--brand)", marginBottom: 4 },
  title: { fontSize: 26, fontWeight: 800, color: "var(--ink)", margin: 0, letterSpacing: -0.3 },
  subtitle: { fontSize: 13, color: "var(--muted)" },
  weekNav: { display: "flex", alignItems: "center", gap: 8, marginTop: 6 },
  weekNavBtn: {
    display: "flex", alignItems: "center", justifyContent: "center", width: 24, height: 24,
    padding: 0, flexShrink: 0,
    border: "1px solid var(--line)", borderRadius: 7, background: "var(--panel, #fff)",
    color: "var(--ink)", cursor: "pointer",
  },
  weekNavThisWeek: {
    fontSize: 11, fontWeight: 600, color: "var(--brand)", background: "rgba(29, 111, 216,0.1)",
    border: "1px solid rgba(29, 111, 216,0.3)", borderRadius: 6, padding: "3px 8px", cursor: "pointer",
  },
  fleetStrip: { display: "flex", gap: 22 },
  fleetStat: { display: "flex", alignItems: "center", gap: 8, color: "var(--muted)" },
  fleetStatValue: { fontSize: 16, fontWeight: 800, color: "var(--ink)", lineHeight: 1.1, fontVariantNumeric: "tabular-nums" },
  fleetStatLabel: { fontSize: 10, color: "var(--muted)", marginTop: 1 },
  boardTabs: { display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" },
  boardTab: {
    display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, fontWeight: 700,
    color: "var(--muted)", background: "var(--panel, #fff)", border: "1px solid var(--line)",
    borderRadius: 999, padding: "8px 14px", cursor: "pointer",
  },
  boardTabActive: {
    color: "var(--brand)", background: "rgba(29, 111, 216,0.08)", borderColor: "rgba(29, 111, 216,0.35)",
  },
  infoWrap: { position: "relative", display: "inline-flex", marginLeft: 2 },
  infoBtn: {
    display: "flex", alignItems: "center", justifyContent: "center", width: 16, height: 16,
    borderRadius: "50%", border: "1px solid var(--line)", background: "var(--bg)",
    color: "var(--muted)", cursor: "pointer", padding: 0,
  },
  infoBtnActive: { color: "var(--brand)", borderColor: "var(--brand)", background: "rgba(29, 111, 216,0.08)" },
  infoPopover: {
    position: "absolute", top: "calc(100% + 8px)", width: 260, zIndex: 20,
    background: "var(--panel, #fff)", border: "1px solid var(--line)", borderRadius: 10,
    boxShadow: "var(--shadow-lg, 0 10px 28px rgba(9,20,19,0.14))", padding: "10px 12px",
    fontSize: 12, fontWeight: 500, lineHeight: 1.5, color: "var(--ink-2, var(--ink))",
    textTransform: "none", letterSpacing: 0, whiteSpace: "normal",
  },
  leaderStrip: {
    display: "flex", alignItems: "center", gap: 10, marginBottom: 16,
    padding: "10px 16px", borderRadius: 12,
    background: "linear-gradient(90deg, rgba(244,201,74,0.12), rgba(244,201,74,0.02))",
    border: "1px solid rgba(244,201,74,0.3)",
  },
  leaderIconWrap: {
    width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
    background: "linear-gradient(135deg, #f4c94a, #d99c1f)", color: "#3a2a08",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  leaderText: { fontSize: 13, color: "var(--ink-2, var(--ink))" },
  leaderName: { fontWeight: 800, color: "var(--ink)" },
  leaderValue: { fontWeight: 800, color: "var(--brand)" },
  leaderGap: { color: "var(--muted)" },
  board: { display: "flex", flexDirection: "column", gap: 12 },
  emptyState: {
    padding: "28px 20px", textAlign: "center", color: "var(--muted)", fontSize: 13,
    background: "var(--panel, #fff)", border: "1px dashed var(--line)", borderRadius: 12,
  },
  card: {
    background: "var(--panel, #fff)", border: "1px solid var(--line)", borderRadius: 14,
    overflow: "hidden", transition: "box-shadow 0.15s",
  },
  cardTop: { border: "1px solid rgba(244,201,74,0.55)", boxShadow: "0 2px 14px rgba(244,201,74,0.12)" },
  cardHead: {
    display: "flex", alignItems: "center", gap: 16, padding: "14px 18px",
  },
  dispatcherName: { fontSize: 15, fontWeight: 700, color: "var(--ink)", marginBottom: 6 },
  bookedSubline: { fontSize: 11.5, color: "var(--muted)", marginTop: 6 },
  trendBadge: {
    display: "inline-flex", alignItems: "center", gap: 1, fontSize: 10, fontWeight: 700,
    marginTop: 2,
  },
  dayBarRow: { display: "flex", gap: 7, marginTop: 12, height: 46 },
  dayBarCol: { display: "flex", flexDirection: "column", alignItems: "center", gap: 5, width: 16 },
  dayBarTrack: {
    width: 10, height: 34, display: "flex", alignItems: "flex-end",
    background: "var(--bg)", borderRadius: "5px 5px 0 0",
    borderBottom: "1.5px solid var(--line)", overflow: "hidden",
  },
  dayBarFill: { width: "100%", borderRadius: "4px 4px 0 0", transition: "height 0.4s ease, background 0.4s ease" },
  dayBarLabel: { fontSize: 9.5, color: "var(--muted)", fontWeight: 600, letterSpacing: 0.2 },
  dayBarLabelPeak: { color: "var(--ink)", fontWeight: 800 },
  raceTrack: { height: 7, borderRadius: 4, background: "var(--bg)", overflow: "hidden" },
  raceFill: { height: "100%", borderRadius: 4, transition: "width 0.6s cubic-bezier(0.22,1,0.36,1)" },
  statBlock: { textAlign: "right", minWidth: 78 },
  statBig: { fontSize: 15, fontWeight: 800, color: "var(--ink)", fontVariantNumeric: "tabular-nums" },
  statSmallLabel: { fontSize: 9.5, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.4, marginTop: 1 },
  chevronWrap: { color: "var(--muted)", flexShrink: 0 },
  truckList: { padding: "4px 18px 16px 74px", display: "flex", flexDirection: "column", gap: 10 },
  truckRow: { display: "flex", flexDirection: "column", gap: 4 },
  truckRowHead: { display: "flex", alignItems: "center", gap: 10, fontSize: 12 },
  truckIconWrap: { color: "var(--muted)", display: "flex" },
  truckUnit: { fontWeight: 700, color: "var(--ink)", minWidth: 46 },
  truckMiles: { color: "var(--ink-2, var(--ink))", fontVariantNumeric: "tabular-nums", minWidth: 66 },
  truckRate: { color: "var(--muted)", fontVariantNumeric: "tabular-nums", minWidth: 60 },
  truckLoads: { color: "var(--muted)", marginLeft: "auto" },
  rankStack: { display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flexShrink: 0 },
  rankChangeBadge: {
    display: "inline-flex", alignItems: "center", gap: 1, fontSize: 9, fontWeight: 800,
    borderRadius: 999, padding: "1px 5px",
  },
  loadList: { padding: "4px 18px 16px 74px", display: "flex", flexDirection: "column", gap: 8 },
  loadListEmpty: { fontSize: 12, color: "var(--muted)" },
  loadRowHead: {
    display: "flex", alignItems: "center", gap: 10, fontSize: 9.5, fontWeight: 700,
    color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.4, paddingBottom: 4,
    borderBottom: "1px solid var(--line)",
  },
  loadRow: {
    display: "flex", alignItems: "center", gap: 10, fontSize: 12, paddingBottom: 8,
    borderBottom: "1px dashed var(--line)",
  },
  loadRowLane: { flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 8 },
  loadRowUnit: { fontWeight: 700, color: "var(--ink)", flexShrink: 0 },
  loadRowRoute: {
    color: "var(--ink-2, var(--ink))", overflow: "hidden", textOverflow: "ellipsis",
    whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 3,
  },
  loadRowArrow: { transform: "rotate(90deg)", color: "var(--muted)", flexShrink: 0 },
  loadRowDate: { color: "var(--muted)", minWidth: 46, fontVariantNumeric: "tabular-nums" },
  loadRowMiles: { color: "var(--muted)", minWidth: 60, fontVariantNumeric: "tabular-nums" },
  loadRowRate: { color: "var(--muted)", minWidth: 56, fontVariantNumeric: "tabular-nums" },
  loadRowRevenue: { color: "var(--ink)", fontWeight: 700, minWidth: 64, textAlign: "right", fontVariantNumeric: "tabular-nums" },
};
